import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Platform } from '@angular/cdk/platform';
import { Observable, map } from 'rxjs';
import { jsonReplacer } from '@utils/json-replacer';

import { environment } from '../../../environments/environment';
import { ActivityModel } from '@app/models/activity.model';

type ValidType =
  | 'task_title'
  | 'task_description'
  | 'offer_message'
  | 'offer_price_increase_message'
  | 'offer_comment'
  | 'task_comment'
  | 'review_message'
  | 'user_description'
  | 'user_education';

interface SaveOriginalTextRequest {
  type: ValidType;
  text: string;
  resId?: number;
  source: 'regex' | 'manual' | 'ml';
}

export interface AccountLimitsResponse {
  cancellationRate: number | null;
  numOffersMadeToday: number;
  taskBudgetLimit: number | null;
  offerCountLimit: number | null;
  nextIn: number;
  nextTaskBudgetLimit: number | null;
  nextOfferCountLimit: number | null;
}

export interface contentFeed {
  blogs: any[];
  guides: any[];
  priceGuides: any[];
}

@Injectable()
export class MiscService {
  baseHref = `${environment.apiUrl}/misc`;
  dashBoardHref = environment.dashboardBaseRef;

  constructor(
    private http: HttpClient,
    private platform: Platform,
  ) {}

  saveOriginalText(
    data: SaveOriginalTextRequest | SaveOriginalTextRequest[],
  ): Observable<any> {
    const url = `${this.baseHref}/original-text`;
    return this.http.post(url, data);
  }

  requestOpenAIForSummarizingRes(taskData: any): Observable<any> {
    const url = `${this.dashBoardHref}api/ai/summarize`;
    return this.http.post(url, taskData);
  }

  addResourceIdToEntry(id: number | number[], resId: number) {
    const url = `${this.baseHref}/resource-id`;
    return this.http.post(url, { id, resId });
  }

  getCommunityGuidelinesTo(): Observable<any> {
    return this.http.get(
      `${this.dashBoardHref}api/other/community-guidelines-to`,
    );
  }

  getCommunityGuidelinesHh(): Observable<any> {
    return this.http.get(
      `${this.dashBoardHref}api/other/community-guidelines-hh`,
    );
  }

  getInsurance(countryCode: string): Observable<any> {
    const url = `${this.dashBoardHref}api/other/insurance/${countryCode}`;
    return this.http.get(url);
  }

  getContentFeed(): Observable<contentFeed> {
    const url = `${this.dashBoardHref}api/other/content-feed`;
    return this.http.get<contentFeed>(url);
  }

  generateTrustpilotUniqueLinks(): Observable<any> {
    const url = `${this.baseHref}/trust-pilot-unique-link`;
    return this.http.get(url);
  }

  getPaintCalculator(lang: string): Observable<any> {
    const url = `${this.dashBoardHref}api/other/paint-calculator/${lang}`;
    return this.http.get(url);
  }

  saveHash(data: string): Observable<any> {
    const url = `${this.baseHref}/save-hash`;
    return this.http.post(url, { hash: data });
  }

  calculateTaskOwnerFee(data: { priceBid: number }): Observable<any> {
    const url = `${this.baseHref}/taskowner-fee`;
    return this.http.post(url, data);
  }

  logError(data: any, file: string): Observable<any> {
    data.platform = {
      android: this.platform.ANDROID,
      blink: this.platform.BLINK,
      edge: this.platform.EDGE,
      firefox: this.platform.FIREFOX,
      ios: this.platform.IOS,
      safari: this.platform.SAFARI,
      trident: this.platform.TRIDENT,
      webkit: this.platform.WEBKIT,
      isBrowser: this.platform.isBrowser,
    };
    const url = `${this.baseHref}/log-error${file ? `?file=${file}` : ''}`;
    return this.http.post(url, { data: JSON.stringify(data, jsonReplacer) });
  }

  sendSms(phone: string, message: string): Observable<any> {
    const url = `${this.baseHref}/send-sms/${phone}`;
    return this.http.post(url, { message });
  }

  getAccountLimits(): Observable<AccountLimitsResponse> {
    const url = `${this.baseHref}/handyhander-limits`;
    return this.http.get<AccountLimitsResponse>(url);
  }

  getLastActivities(): Observable<ActivityModel[]> {
    const url = `${this.baseHref}/last-activities`;
    return this.http.get<ActivityModel[]>(url).pipe(
      map((activities) =>
        activities.map(
          (activity) =>
            new ActivityModel({
              ...activity,
              date: new Date(activity.date),
            }),
        ),
      ),
    );
  }

  createInsuranceClaim(data: any): Observable<any> {
    const url = `${this.baseHref}/insurance`;
    return this.http.post(url, data);
  }

  recordSession(): Observable<any> {
    const url = `${this.baseHref}/session`;
    return this.http.get(url);
  }
}
